import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera } from 'lucide-react';

import { insightsApi } from '../../lib/insightsApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle } from '../../lib/libelles';
import { formatNombre } from '../../lib/formats';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

/**
 * « Photographier l'index » d'un pistolet (lots 030 et 037).
 *
 * La photo du compteur est envoyée au serveur, qui rend un index PROPOSÉ ; rien
 * n'est enregistré avant que le gérant le confirme avec « Utiliser » (le relevé
 * porte alors la photo et la source « Photo »). Lecture impossible : la photo
 * reste jointe et l'index se saisit à la main.
 *
 * `onUtiliser({ index, documentId })` reçoit l'index confirmé.
 */

/** Choix proposés après une lecture : l'index retenu puis ses alternatives. */
export const choixDeLecture = (lecture) => {
  if (!lecture || !['READ', 'AMBIGUOUS'].includes(lecture.status)) return [];
  const valeurs = [lecture.index, ...(lecture.alternatives || [])].filter(
    (valeur) => valeur !== null && valeur !== undefined && Number.isFinite(Number(valeur)),
  );
  return [...new Set(valeurs.map(Number))];
};

export const PhotoIndex = ({ stationId, nozzleId, kind = 'CLOSING', onUtiliser, disabled = false }) => {
  const champ = useRef(null);
  const [enCours, setEnCours] = useState(false);
  const [lecture, setLecture] = useState(null);

  const lire = async (event) => {
    const fichier = event.target.files && event.target.files[0];
    // Le même fichier doit pouvoir être rechoisi.
    event.target.value = '';
    if (!fichier) return;
    setEnCours(true);
    try {
      setLecture(await insightsApi.lirePhoto(stationId, { nozzleId, kind, fichier }));
    } catch (probleme) {
      setLecture(null);
      toast.error(messageErreur(probleme, 'Impossible de lire la photo'));
    } finally {
      setEnCours(false);
    }
  };

  const choix = choixDeLecture(lecture);

  return (
    <div className="flex flex-col items-end gap-1" data-testid={`photo-index-${nozzleId}`}>
      <input ref={champ} type="file" accept="image/*" capture="environment" className="hidden" onChange={lire} aria-hidden="true" tabIndex={-1} />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled || enCours}
        onClick={() => champ.current && champ.current.click()}
      >
        <Camera className="h-4 w-4" aria-hidden="true" />
        {enCours ? 'Lecture…' : 'Photographier l’index'}
      </Button>
      {lecture && choix.length === 0 && (
        <p className="text-xs text-muted-foreground" role="status">
          {libelle('station_lecture_photo', lecture.status)}
        </p>
      )}
      {choix.length > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-1" role="status">
          <span className="text-xs text-muted-foreground">
            {choix.length > 1 ? libelle('station_lecture_photo', 'AMBIGUOUS') : 'Index proposé'}
          </span>
          {lecture.confidence !== 'HIGH' && <Badge variant="warning">À vérifier</Badge>}
          {choix.map((valeur) => (
            <Button
              key={valeur}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                onUtiliser({ index: valeur, documentId: lecture.photo_document_id || null });
                setLecture(null);
              }}
            >
              Utiliser {formatNombre(valeur, 1)}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoIndex;
